import React from 'react';
import { PencilRulerIcon } from '@phosphor-icons/react';
import { IniciativaOverlayCard } from './IniciativaOverlayCard';
import { IniciativasCarouselMobile } from './IniciativasCarouselMobile';
import {
  layoutIniciativas,
  toIniciativaCards,
  type IniciativasData,
} from './helpers';
import { TITULO_FAIXA } from './typography';

interface IniciativasSectionProps {
  data?: IniciativasData;
  isEditMode?: boolean;
  /** Presente só no editor: recebe o índice do card em `data.items`. */
  onEditItem?: (index: number) => void;
}

export const INICIATIVAS_TITLE = 'Iniciativas e Projetos';

/**
 * Faixa "Iniciativas e Projetos" (bloco `mpfIniciativas`).
 *
 * Bento de três posições: o primeiro item curado é o card alto da esquerda e
 * os dois seguintes empilham à direita. Sem curadoria a faixa não é
 * renderizada — a seleção é o conteúdo dela inteiro, não há fallback
 * automático como na grade de destaques.
 */
export const IniciativasSection: React.FC<IniciativasSectionProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  const cards = React.useMemo(() => toIniciativaCards(data?.items), [data]);

  if (cards.length === 0) return null;

  const { hero, small } = layoutIniciativas(cards);

  return (
    /* Mesma moldura das outras faixas da capa: traço, raio 16 e padding 16,
       com os 16 entre cabeçalho e grade saindo do `gap`. `h-full` porque a
       faixa divide a linha com o carrossel de destaques e as duas colunas
       precisam terminar juntas.
       `not-prose` porque a seção também é usada como bloco numa página comum,
       onde o `prose` do corpo do conteúdo vazaria para dentro — a explicação
       inteira está em `AcompanheRedesSection`. */
    <section
      aria-labelledby="capa-iniciativas-titulo"
      className="not-prose flex h-full flex-col gap-4 rounded-2xl border border-gray-200 bg-surface-container-lowest p-4 dark:border-night-850 dark:bg-night-800"
    >
      <div className="flex items-center gap-2">
        {/* Cor por valor arbitrário, e não `text-yellow-500`: a paleta
            `yellow` do Tailwind não é redirecionada para os tokens, e a dele
            (#EAB308) é bem mais limão que o dourado do design (#C27801). */}
        <PencilRulerIcon
          aria-hidden="true"
          className="h-6 w-6 shrink-0 text-[color:var(--yellow-500)] dark:text-white"
        />
        <h3
          id="capa-iniciativas-titulo"
          className={`${TITULO_FAIXA} text-blue-800 dark:text-white`}
        >
          {data?.title || INICIATIVAS_TITLE}
        </h3>
      </div>

      {/* Régua na mesma cor do ícone: decorativa, não separa conteúdo
          semântico — o cabeçalho já é o rótulo da seção. */}
      <div
        aria-hidden="true"
        className="h-0.5 w-full shrink-0 bg-[color:var(--yellow-500)] dark:!bg-night-750"
      />

      {/* Duas formas de DOM, não duas visibilidades do mesmo: no desktop os
          três cards são um bento — duas colunas, duas linhas de altura igual
          (as trilhas `1fr` se equalizam pelo card mais alto) e o hero ocupando
          a coluna da esquerda inteira; no mobile viram slides de um carrossel.
          Quem liga e desliga é só CSS, sem JS medindo a tela.

          O corte é `lg` (992px): no mobile e no tablet (`md` até `lg`) a faixa
          é um carrossel — um card por vez no celular, ~2 com o terceiro
          espiando no tablet —, e só do desktop em diante vira bento. */}
      <div className="hidden flex-1 gap-4 lg:grid lg:grid-cols-2 lg:grid-rows-2">
        {hero && (
          <IniciativaOverlayCard
            card={hero}
            variant="hero"
            isEditMode={isEditMode}
            onEditItem={onEditItem}
            // Sem os cards menores ao lado, o hero ocupa a linha inteira em
            // vez de deixar meia grade vazia.
            className={`lg:row-span-2 lg:h-full ${
              small.length > 0 ? '' : 'lg:col-span-2'
            }`}
          />
        )}

        {small.map((card) => (
          <IniciativaOverlayCard
            key={card.key}
            card={card}
            variant="small"
            isEditMode={isEditMode}
            onEditItem={onEditItem}
            className="lg:h-full lg:min-h-[15rem]"
          />
        ))}
      </div>

      {/* O lápis vai junto: cada card do carrossel é o mesmo item curado do
          bento, com o `sourceIndex` próprio — quem edita no mobile edita o
          item certo. */}
      <div className="lg:hidden">
        <IniciativasCarouselMobile
          cards={cards}
          isEditMode={isEditMode}
          onEditItem={onEditItem}
        />
      </div>
    </section>
  );
};

export default IniciativasSection;
